import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import usePageMeta from '@/hooks/usePageMeta';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import RichText from '@/components/RichText';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  BookOpen, CheckCircle, Circle, Play, ExternalLink, FileText,
  Clock, Award, ArrowLeft, ChevronRight, Lock, Download, RotateCcw,
  AlertCircle, Star
} from 'lucide-react';
import { toast } from 'sonner';
import { SponsorBanner } from '@/components/SponsorBanner';
import PageSideRail from '@/components/PageSideRail';

const BACKEND_URL = '';

function ModuleView({ module, isCompleted, onComplete }) {
  return (
    <div className="space-y-6" data-testid={`module-content-${module.module_id}`}>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">{module.title}</h2>
          {module.duration && <p className="text-sm text-slate-500 mt-1"><Clock className="inline h-3.5 w-3.5 mr-1" />{module.duration}</p>}
        </div>
        {isCompleted ? (
          <span className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 text-sm font-medium rounded-full">
            <CheckCircle className="h-4 w-4" /> Completed
          </span>
        ) : (
          <Button data-testid={`complete-module-${module.module_id}`} onClick={onComplete} className="bg-gradient-to-r from-brand-navy to-brand-sage text-white">
            <CheckCircle className="h-4 w-4 mr-2" /> Mark Complete
          </Button>
        )}
      </div>

      {module.video_url && (
        <Card className="p-0 overflow-hidden border-0 bg-black aspect-video">
          {module.video_url.includes('youtube.com') || module.video_url.includes('youtu.be') ? (
            <iframe
              src={module.video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={module.title}
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <a href={module.video_url} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 text-white hover:text-brand-green transition-colors">
                <Play className="h-12 w-12" />
                <span className="text-lg">Watch Video</span>
              </a>
            </div>
          )}
        </Card>
      )}

      {module.content && (
        <Card className="p-6">
          <div className="prose prose-slate max-w-none text-sm leading-relaxed whitespace-pre-wrap">
            {module.content}
          </div>
        </Card>
      )}

      {module.resources?.length > 0 && (
        <Card className="p-5">
          <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3">Resources</h4>
          <div className="space-y-2">
            {module.resources.map((r, i) => (
              <a key={i} href={r.url} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors group"
                data-testid={`resource-${i}`}>
                <ExternalLink className="h-4 w-4 text-brand-blue flex-shrink-0" />
                <span className="text-sm text-slate-700 group-hover:text-brand-navy">{r.title || r.url}</span>
              </a>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function ExamView({ exam, progress, onSubmit, courseTitle, courseId, onRated }) {
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const lastAttempt = progress?.exam_attempts?.length > 0
    ? progress.exam_attempts[progress.exam_attempts.length - 1]
    : null;

  const handleSubmit = async () => {
    if (Object.keys(answers).length < exam.questions.length) {
      toast.error('Please answer all questions before submitting');
      return;
    }
    setSubmitting(true);
    try {
      const res = await onSubmit(answers);
      setResult(res);
    } catch (e) {
      toast.error(e.message);
    }
    setSubmitting(false);
  };

  if (result) {
    const attempt = result.attempt;
    return (
      <div className="space-y-6" data-testid="exam-results">
        <Card className={`p-8 text-center ${attempt.passed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${attempt.passed ? 'bg-green-100' : 'bg-red-100'}`}>
            {attempt.passed ? <Award className="h-10 w-10 text-green-600" /> : <AlertCircle className="h-10 w-10 text-red-600" />}
          </div>
          <h2 className="text-2xl font-bold mb-2" data-testid="exam-score">
            {attempt.passed ? 'Congratulations!' : 'Not quite there yet'}
          </h2>
          <p className="text-4xl font-bold mb-2" style={{ color: attempt.passed ? '#22c55e' : '#ef4444' }}>
            {attempt.score}%
          </p>
          <p className="text-sm text-slate-600 mb-1">
            {attempt.earned_points} / {attempt.total_points} points
          </p>
          <p className="text-sm text-slate-500">
            Pass mark: {exam.pass_mark}%
          </p>

          {attempt.passed && result.certificate && (
            <div className="mt-6">
              <p className="text-sm text-green-700 font-medium mb-1">Your certificate has been issued!</p>
              {result.certificate.cpd_credits > 0 && (
                <p className="text-sm text-green-600 mb-3">{result.certificate.cpd_credits} CPD credit{result.certificate.cpd_credits !== 1 ? 's' : ''} awarded</p>
              )}
              <p className="text-xs text-slate-500 mb-3">Certificate No: {result.certificate.certificate_number}</p>
              <Button data-testid="view-certificate-btn" onClick={() => window.location.reload()}
                className="bg-gradient-to-r from-brand-navy to-brand-sage text-white">
                <Award className="h-4 w-4 mr-2" /> View Certificate
              </Button>
            </div>
          )}

          {!attempt.passed && (
            <Button data-testid="retake-exam-btn" onClick={() => { setResult(null); setAnswers({}); }}
              className="mt-6" variant="outline">
              <RotateCcw className="h-4 w-4 mr-2" /> Retake Exam
            </Button>
          )}
        </Card>

        {/* Rating prompt after passing */}
        {attempt.passed && courseId && (
          <CourseRatingWidget courseId={courseId} onRated={onRated} />
        )}

        {/* Show answer review */}
        <Card className="p-5">
          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">Answer Review</h3>
          <div className="space-y-4">
            {attempt.results.map((r, i) => {
              const q = exam.questions.find(qq => qq.question_id === r.question_id);
              return (
                <div key={r.question_id} className={`p-4 rounded-lg ${r.is_correct ? 'bg-green-50' : 'bg-red-50'}`}>
                  <div className="flex items-start gap-2">
                    {r.is_correct ? <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" /> : <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />}
                    <div>
                      <p className="text-sm font-medium text-slate-800">{q?.question || `Question ${i + 1}`}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Your answer: <span className="font-medium">{q?.options?.[r.user_answer] || 'No answer'}</span>
                        {!r.is_correct && <> &middot; Correct: <span className="font-medium text-green-700">{q?.options?.[r.correct_answer]}</span></>}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="exam-form">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Course Exam</h2>
          <p className="text-sm text-slate-500 mt-1">{exam.questions.length} questions &middot; Pass mark: {exam.pass_mark}%</p>
        </div>
        {lastAttempt && (
          <span className={`px-3 py-1.5 text-xs font-medium rounded-full ${lastAttempt.passed ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
            Last attempt: {lastAttempt.score}%
          </span>
        )}
      </div>

      <div className="space-y-5">
        {exam.questions.map((q, qi) => (
          <Card key={q.question_id} className="p-5" data-testid={`exam-question-${qi}`}>
            <p className="text-sm font-bold text-slate-800 mb-3">
              <span className="text-brand-navy mr-2">Q{qi + 1}.</span>
              {q.question}
              <span className="text-xs font-normal text-slate-400 ml-2">({q.points || 10} pts)</span>
            </p>
            <div className="space-y-2">
              {q.options.map((opt, oi) => (
                <label key={oi}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    answers[q.question_id] === oi
                      ? 'border-brand-navy bg-brand-navy/5'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                  data-testid={`answer-${qi}-${oi}`}>
                  <input type="radio" name={q.question_id} value={oi} className="sr-only"
                    checked={answers[q.question_id] === oi}
                    onChange={() => setAnswers(prev => ({ ...prev, [q.question_id]: oi }))} />
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    answers[q.question_id] === oi ? 'border-brand-navy bg-brand-navy' : 'border-slate-300'
                  }`}>
                    {answers[q.question_id] === oi && <div className="w-2 h-2 bg-white rounded-full" />}
                  </div>
                  <span className="text-sm text-slate-700">{opt}</span>
                </label>
              ))}
            </div>
          </Card>
        ))}
      </div>

      <Button data-testid="submit-exam-btn" onClick={handleSubmit} disabled={submitting}
        className="w-full h-12 bg-gradient-to-r from-brand-navy to-brand-sage text-white text-base">
        {submitting ? 'Submitting...' : `Submit Exam (${Object.keys(answers).length}/${exam.questions.length} answered)`}
      </Button>
    </div>
  );
}

function CertificateView({ certificate, courseTitle }) {
  return (
    <div className="space-y-6" data-testid="certificate-view">
      <Card className="p-10 text-center bg-gradient-to-br from-white to-brand-navy/5 border-2 border-brand-navy/20 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-brand-navy via-brand-sage to-brand-green" />
        <div className="absolute top-4 right-4 opacity-10">
          <Award className="h-32 w-32 text-brand-navy" />
        </div>
        <div className="relative">
          <p className="text-xs uppercase tracking-[0.3em] text-brand-navy/60 mb-2">Certificate of Completion</p>
          <h2 className="text-3xl font-bold text-brand-navy mb-1">SLL Community</h2>
          <div className="w-16 h-0.5 bg-brand-sage mx-auto my-4" />
          <p className="text-sm text-slate-500 mb-2">This certifies that</p>
          <p className="text-2xl font-bold text-slate-800 mb-2" data-testid="cert-user-name">{certificate.user_name}</p>
          <p className="text-sm text-slate-500 mb-2">has successfully completed</p>
          <p className="text-xl font-bold text-brand-navy mb-4" data-testid="cert-course-title">{certificate.course_title || courseTitle}</p>

          {certificate.cpd_credits > 0 && (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-sage/10 rounded-full border border-brand-sage/30 mb-4" data-testid="cert-cpd-credits">
              <Award className="h-4 w-4 text-brand-sage" />
              <span className="text-sm font-semibold text-brand-sage">{certificate.cpd_credits} CPD Credit{certificate.cpd_credits !== 1 ? 's' : ''} Awarded</span>
            </div>
          )}

          <div className="flex items-center justify-center gap-6 text-sm text-slate-600">
            <span>Score: <strong className="text-brand-navy">{certificate.score}%</strong></span>
            <span>Pass mark: {certificate.pass_mark}%</span>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-200/50 space-y-1">
            <p className="text-sm font-medium text-slate-700" data-testid="cert-number">
              Certificate No: {certificate.certificate_number || certificate.certificate_id}
            </p>
            <p className="text-xs text-slate-400">
              Issued: {new Date(certificate.issued_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}

function CourseRatingWidget({ courseId, existingRating, onRated }) {
  const [hovered, setHovered] = useState(0);
  const [selected, setSelected] = useState(existingRating?.rating || 0);
  const [review, setReview] = useState(existingRating?.review || '');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(!!existingRating);

  const handleSubmit = async () => {
    if (selected === 0) { toast.error('Please select a rating'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/courses/${courseId}/rate`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating: selected, review: review.trim() }),
      });
      if (!res.ok) throw new Error('Failed to submit rating');
      const data = await res.json();
      toast.success('Thank you for your rating!');
      setSubmitted(true);
      if (onRated) onRated(data);
    } catch (e) { toast.error(e.message); }
    setSubmitting(false);
  };

  return (
    <Card className="p-6 border-brand-sage/30 bg-gradient-to-br from-white to-brand-sage/5" data-testid="course-rating-widget">
      <div className="text-center">
        <Award className="h-8 w-8 text-brand-sage mx-auto mb-2" />
        <h3 className="text-lg font-bold text-slate-800 mb-1" data-testid="rating-title">
          {submitted ? 'Your Rating' : 'Rate This Course'}
        </h3>
        <p className="text-sm text-slate-500 mb-4">
          {submitted ? 'Thanks for your feedback!' : 'How would you rate your learning experience?'}
        </p>

        <div className="flex items-center justify-center gap-1.5 mb-4" data-testid="star-rating-selector">
          {[1, 2, 3, 4, 5].map(star => (
            <button key={star}
              data-testid={`rate-star-${star}`}
              onMouseEnter={() => !submitted && setHovered(star)}
              onMouseLeave={() => !submitted && setHovered(0)}
              onClick={() => { if (!submitted) setSelected(star); }}
              disabled={submitted}
              className={`transition-transform ${submitted ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}>
              <Star className={`h-8 w-8 transition-colors ${
                star <= (hovered || selected)
                  ? 'fill-amber-400 text-amber-400'
                  : 'text-slate-200'
              }`} />
            </button>
          ))}
        </div>

        {selected > 0 && !submitted && (
          <div className="space-y-3 animate-in fade-in slide-in-from-bottom-2">
            <textarea
              data-testid="rating-review-input"
              value={review}
              onChange={e => setReview(e.target.value)}
              placeholder="Share your experience (optional)..."
              rows={3}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-sage/50 resize-none"
            />
            <Button data-testid="submit-rating-btn" onClick={handleSubmit} disabled={submitting}
              className="bg-gradient-to-r from-brand-navy to-brand-sage text-white">
              {submitting ? 'Submitting...' : 'Submit Rating'}
            </Button>
          </div>
        )}

        {submitted && existingRating?.review && (
          <p className="text-sm text-slate-600 italic mt-2" data-testid="rating-review-text">"{existingRating.review}"</p>
        )}
        {submitted && review && !existingRating?.review && (
          <p className="text-sm text-slate-600 italic mt-2" data-testid="rating-review-text">"{review}"</p>
        )}
      </div>
    </Card>
  );
}

export default function CourseDetailPage({ user }) {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  usePageMeta(course ? {
    title: course.title || 'Course',
    description: (course.description || course.summary || '').slice(0, 200),
    image: course.thumbnail || course.image_url,
  } : { title: 'Course' });
  const [loading, setLoading] = useState(true);
  const [activeModule, setActiveModule] = useState(null);
  const [activeTab, setActiveTab] = useState('study');
  const [enrolling, setEnrolling] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState('');

  const loadCourse = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/courses/${courseId}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load course');
      const data = await res.json();
      if (data.coming_soon) {
        toast.error('This course is coming soon and not yet available.');
        navigate('/courses');
        return;
      }
      setCourse(data);
      if (data.user_progress && data.modules?.length > 0 && !activeModule) {
        // Auto-select first incomplete module, or first module
        const completed = data.user_progress.completed_modules || [];
        const firstIncomplete = data.modules.find(m => !completed.includes(m.module_id));
        setActiveModule(firstIncomplete?.module_id || data.modules[0]?.module_id);
      }
    } catch (e) {
      toast.error('Failed to load course');
    }
    setLoading(false);
  }, [courseId]);

  useEffect(() => { loadCourse(); }, [loadCourse]);

  const handleCompleteModule = async (moduleId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/courses/${courseId}/modules/${moduleId}/complete`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setCourse(prev => ({
        ...prev,
        user_progress: { ...prev.user_progress, completed_modules: data.completed_modules, progress_percent: data.progress_percent }
      }));
      toast.success('Module completed!');
      // Auto-advance to next module
      const idx = course.modules.findIndex(m => m.module_id === moduleId);
      if (idx < course.modules.length - 1) {
        setActiveModule(course.modules[idx + 1].module_id);
      }
    } catch { toast.error('Failed to complete module'); }
  };

  const handleExamSubmit = async (answers) => {
    const res = await fetch(`${BACKEND_URL}/api/courses/${courseId}/exam/submit`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ answers }),
    });
    if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Submission failed'); }
    const data = await res.json();
    // Refresh course data to get updated progress + certificate
    await loadCourse();
    return data;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation user={user} />
        <main className="pt-24 pb-12 px-6">
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full" />
          </div>
        </main>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation user={user} />
        <main className="pt-24 pb-12 px-6 text-center">
          <p className="text-slate-500">Course not found</p>
          <Button onClick={() => navigate('/courses')} variant="outline" className="mt-4">Back to Courses</Button>
        </main>
      </div>
    );
  }

  const isInPerson = course.delivery_mode === 'in-person';
  const isEnrolled = !!course.user_progress;
  const progress = course.user_progress;
  const completedModules = progress?.completed_modules || [];
  // For in-person workshops we ignore any legacy `modules`/`exam` data so the
  // public page renders the workshop template (trainer / date / venue) and the
  // user can't accidentally "complete" a module on a course that's delivered
  // face-to-face.
  const showModules = !isInPerson && (course.modules?.length || 0) > 0;
  const allModulesComplete = showModules && completedModules.length >= course.modules.length;
  const hasExam = !isInPerson && course.exam && (course.exam.questions?.length > 0 || course.exam.question_count > 0);
  const hasCertificate = !!course.certificate;
  const hasPassed = progress?.exam_attempts?.some(a => a.passed);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <main className="pt-24 pb-12 px-4 sm:px-6">
        <div className="container mx-auto max-w-7xl">
          <SponsorBanner placement="courses-descriptor" type="top" className="mb-4 flex justify-center" />
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="mb-6">
            <button onClick={() => navigate('/courses')} className="flex items-center gap-1 text-sm text-slate-500 hover:text-brand-navy mb-3 transition-colors">
              <ArrowLeft className="h-4 w-4" /> Back to Courses
            </button>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2.5 py-0.5 bg-brand-sage/20 text-brand-sage text-xs font-medium rounded-full">{course.level}</span>
                  {course.topic_tags?.map(t => (
                    <span key={t} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded">{t}</span>
                  ))}
                </div>
                <h1 className="text-3xl font-bold font-heading text-slate-800" data-testid="course-title">{course.title}</h1>
                <div className="text-slate-600 mt-1 leading-relaxed">
                  <RichText html={course.description} />
                </div>
              </div>
              {hasCertificate && (
                <span className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 text-sm font-medium rounded-full border border-amber-200">
                  <Award className="h-4 w-4" /> Certified
                </span>
              )}
            </div>

            {/* Progress bar — only meaningful for online/hybrid courses with modules */}
            {isEnrolled && !isInPerson && (
              <div className="mt-4" data-testid="course-progress-bar">
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="text-slate-600">{completedModules.length} / {course.modules?.length || 0} modules completed</span>
                  <span className="font-medium text-brand-navy">{Math.min(100, progress.progress_percent)}%</span>
                </div>
                <Progress value={Math.min(100, progress.progress_percent)} className="h-2" />
              </div>
            )}
            {isEnrolled && isInPerson && (
              <div className="mt-4" data-testid="course-booking-confirmation">
                <Card className="p-4 bg-emerald-50 border border-emerald-200 inline-flex items-center gap-2">
                  <Award className="h-5 w-5 text-emerald-600" />
                  <p className="text-sm font-medium text-emerald-800">You're booked in for this workshop. Details are above.</p>
                </Card>
              </div>
            )}
          </div>

          {!isEnrolled ? (
            /* Not enrolled - overview with enroll option */
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                {course.what_you_will_learn && course.what_you_will_learn.trim() && (
                  <Card className="p-6">
                    <h3 className="text-lg font-bold text-slate-800 mb-3">What you&apos;ll learn</h3>
                    <RichText html={course.what_you_will_learn} className="text-sm text-slate-600 leading-relaxed" />
                  </Card>
                )}
                {(course.delivery_mode === 'in-person' || course.delivery_mode === 'hybrid') && (course.trainer_name || course.venue || course.course_dates) && (
                  <Card className="p-6" data-testid="course-session-details-card">
                    <h3 className="text-lg font-bold text-slate-800 mb-3">Session details</h3>
                    <div className="grid sm:grid-cols-3 gap-4 text-sm">
                      {course.trainer_name && (
                        <div>
                          <p className="text-xs uppercase font-semibold text-slate-400 mb-0.5">Trainer</p>
                          <p className="font-medium text-slate-700">{course.trainer_name}</p>
                        </div>
                      )}
                      {course.venue && (
                        <div>
                          <p className="text-xs uppercase font-semibold text-slate-400 mb-0.5">Venue</p>
                          <p className="font-medium text-slate-700">{course.venue}</p>
                        </div>
                      )}
                      {course.course_dates && (
                        <div>
                          <p className="text-xs uppercase font-semibold text-slate-400 mb-0.5">Date(s)</p>
                          <p className="font-medium text-slate-700">{course.course_dates}</p>
                        </div>
                      )}
                    </div>
                  </Card>
                )}
                <Card className="p-6">
                  <h3 className="text-lg font-bold text-slate-800 mb-3">
                    {isInPerson ? 'What to expect' : 'Course Content'}
                  </h3>
                  {isInPerson ? (
                    <p className="text-sm text-slate-600 leading-relaxed">
                      This is an in-person workshop delivered by the trainer{course.trainer_name ? ` (${course.trainer_name})` : ''}.
                      {course.venue ? ` Held at ${course.venue}` : ''}{course.course_dates ? ` on ${course.course_dates}` : ''}.
                      Book your place to confirm attendance — there is no online module work to complete.
                    </p>
                  ) : (
                    <>
                      <div className="space-y-2">
                        {course.modules?.map((m, i) => (
                          <div key={m.module_id || i} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
                            <div className="w-7 h-7 rounded-full bg-brand-navy/10 text-brand-navy flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-700">{m.title}</p>
                              {m.duration && <p className="text-xs text-slate-400">{m.duration}</p>}
                            </div>
                            <Lock className="h-4 w-4 text-slate-300" />
                          </div>
                        ))}
                      </div>
                      {hasExam && (
                        <div className="mt-3 flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
                          <FileText className="h-5 w-5 text-amber-500" />
                          <div>
                            <p className="text-sm font-medium text-slate-700">Final Exam</p>
                            <p className="text-xs text-slate-500">{course.exam.question_count || course.exam.questions?.length || 0} questions &middot; Pass mark: {course.pass_mark || course.exam.pass_mark || 70}%</p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </Card>
              </div>

              {/* Enroll sidebar */}
              <div className="lg:col-span-1">
                <Card className="p-6 sticky top-24 text-center">
                  {course.price > 0 ? (
                    <p className="text-3xl font-bold text-slate-800 mb-1">£{course.price}</p>
                  ) : (
                    <p className="text-3xl font-bold text-green-600 mb-1">Free</p>
                  )}
                  <p className="text-xs text-slate-500 mb-5">
                    {isInPerson
                      ? `${course.course_dates ? course.course_dates + ' · ' : ''}${course.venue || 'In-person workshop'}`
                      : `${course.modules?.length || 0} modules ${hasExam ? '+ certificate' : ''}`}
                  </p>
                  {course.places_left != null && !course.user_progress && (
                    <p className={`text-xs font-semibold mb-4 ${course.places_left > 0 ? 'text-amber-600' : 'text-red-600'}`} data-testid="course-places-left">
                      {course.places_left > 0 ? `${course.places_left} place${course.places_left === 1 ? '' : 's'} left` : 'This course is full'}
                    </p>
                  )}

                  {/* Session picker — only when the course has multiple dates. */}
                  {Array.isArray(course.sessions) && course.sessions.length > 0 && (
                    <div className="mb-4 text-left" data-testid="session-picker">
                      <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Pick your date</Label>
                      <div className="space-y-1.5">
                        {course.sessions.map(s => {
                          const dateLabel = (() => {
                            try { return new Date(s.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }); }
                            catch { return s.date; }
                          })();
                          const timeRange = [s.start_time, s.end_time].filter(Boolean).join(' – ');
                          const isPicked = selectedSessionId === s.session_id;
                          return (
                            <button key={s.session_id} type="button"
                              onClick={() => setSelectedSessionId(s.session_id)}
                              data-testid={`session-option-${s.session_id}`}
                              className={`w-full text-left px-3 py-2 rounded-lg border transition-all ${isPicked
                                ? 'border-brand-navy bg-brand-navy/5 ring-2 ring-brand-navy/20'
                                : 'border-slate-200 hover:border-brand-navy/40 bg-white'}`}>
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-semibold text-slate-800">{dateLabel}</p>
                                  {(timeRange || s.venue) && (
                                    <p className="text-[10px] text-slate-500 truncate">
                                      {timeRange}{timeRange && s.venue ? ' · ' : ''}{s.venue || (timeRange ? '' : course.venue || '')}
                                    </p>
                                  )}
                                </div>
                                {isPicked && <CheckCircle className="h-4 w-4 text-brand-sage flex-shrink-0" />}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <Button
                    data-testid="enroll-course-btn"
                    onClick={async () => {
                      // For courses with sessions, force the user to pick one.
                      if (Array.isArray(course.sessions) && course.sessions.length > 0 && !selectedSessionId) {
                        toast.error('Please pick a date to attend before enrolling.');
                        return;
                      }
                      setEnrolling(true);
                      try {
                        const isPaid = (course.price || 0) > 0;
                        const alreadyPurchased = (user?.purchased_courses || []).includes(courseId);
                        if (isPaid && !alreadyPurchased) {
                          // Route through Stripe checkout for paid courses
                          const res = await fetch(`${BACKEND_URL}/api/stripe/create-checkout-session`, {
                            method: 'POST', credentials: 'include',
                            headers: { 'Content-Type': 'application/json', 'X-Origin-URL': window.location.origin },
                            body: JSON.stringify({ package_id: 'course', course_id: courseId, session_id: selectedSessionId || null }),
                          });
                          if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Checkout failed'); }
                          const { url } = await res.json();
                          if (url) {
                            window.location.href = url;
                            return;
                          }
                          throw new Error('Stripe did not return a checkout URL');
                        }
                        // Free course or already-purchased — direct enrol
                        const res = await fetch(`${BACKEND_URL}/api/courses/${courseId}/enroll`, {
                          method: 'POST', credentials: 'include',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify(selectedSessionId ? { session_id: selectedSessionId } : {}),
                        });
                        if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Enrollment failed'); }
                        toast.success('Enrolled successfully! Start learning.');
                        await loadCourse();
                      } catch (e) { toast.error(e.message); }
                      setEnrolling(false);
                    }}
                    disabled={enrolling}
                    className="w-full h-12 bg-gradient-to-r from-brand-navy to-brand-sage text-white text-base">
                    {enrolling ? 'Processing…' : isInPerson
                      ? (course.price > 0 ? `Book Place — £${course.price}` : 'Book Free Place')
                      : (course.price > 0 ? `Buy & Enrol — £${course.price}` : 'Enrol Free')}
                  </Button>
                  <div className="mt-4 space-y-2 text-left">
                    {isInPerson ? (
                      <>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <CheckCircle className="h-3.5 w-3.5 text-green-500" /> In-person workshop with live trainer
                        </div>
                        {course.venue && (
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <CheckCircle className="h-3.5 w-3.5 text-green-500" /> Venue: {course.venue}
                          </div>
                        )}
                        {course.course_dates && (
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <CheckCircle className="h-3.5 w-3.5 text-green-500" /> Date(s): {course.course_dates}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <CheckCircle className="h-3.5 w-3.5 text-green-500" /> Full access to all modules
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <CheckCircle className="h-3.5 w-3.5 text-green-500" /> Study at your own pace
                        </div>
                        {hasExam && (
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <CheckCircle className="h-3.5 w-3.5 text-green-500" /> Certificate on completion
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </Card>
              </div>
            </div>
          ) : isInPerson ? (
            /* Enrolled — in-person booking confirmation (no module flow) */
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="inperson-enrolled-view">
              <div className="lg:col-span-2 space-y-4">
                {(course.trainer_name || course.venue || course.course_dates || course.user_progress?.session_id) && (
                  <Card className="p-6">
                    <h3 className="text-lg font-bold text-slate-800 mb-3">Your booking</h3>
                    {(() => {
                      const myDate = course.user_progress?.session_id
                        ? (course.sessions || []).find(s => s.session_id === course.user_progress.session_id)
                        : null;
                      return (
                        <div className="grid sm:grid-cols-3 gap-4 text-sm">
                          {course.trainer_name && (
                            <div>
                              <p className="text-xs uppercase font-semibold text-slate-400 mb-0.5">Trainer</p>
                              <p className="font-medium text-slate-700">{course.trainer_name}</p>
                            </div>
                          )}
                          <div>
                            <p className="text-xs uppercase font-semibold text-slate-400 mb-0.5">Venue</p>
                            <p className="font-medium text-slate-700">{myDate?.venue || course.venue || '—'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase font-semibold text-slate-400 mb-0.5">Your date</p>
                            <p className="font-medium text-slate-700" data-testid="booking-session-date">
                              {myDate
                                ? (() => {
                                    try { return new Date(myDate.date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }); }
                                    catch { return myDate.date; }
                                  })()
                                : (course.course_dates || '—')}
                              {myDate?.start_time && (
                                <span className="block text-xs text-slate-500 mt-0.5">
                                  {myDate.start_time}{myDate.end_time ? ` – ${myDate.end_time}` : ''}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                      );
                    })()}
                  </Card>
                )}
                {course.what_you_will_learn && course.what_you_will_learn.trim() && (
                  <Card className="p-6">
                    <h3 className="text-lg font-bold text-slate-800 mb-3">What you&apos;ll learn</h3>
                    <RichText html={course.what_you_will_learn} className="text-sm text-slate-600 leading-relaxed" />
                  </Card>
                )}
              </div>
              <div className="lg:col-span-1">
                <Card className="p-6 sticky top-24 text-center bg-emerald-50 border-emerald-200">
                  <Award className="h-10 w-10 text-emerald-600 mx-auto mb-2" />
                  <h3 className="text-lg font-bold text-emerald-800 mb-1">You're booked in</h3>
                  <p className="text-sm text-emerald-700">Check your email for booking confirmation and joining details.</p>
                </Card>
              </div>
            </div>
          ) : (
            /* Enrolled - learning view */
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Sidebar - Module list */}
              <div className="lg:col-span-1">
                <Card className="p-0 overflow-hidden sticky top-24">
                  <div className="p-4 bg-brand-navy/5 border-b">
                    <h3 className="text-sm font-bold text-slate-700">Modules</h3>
                  </div>
                  <div className="divide-y max-h-[60vh] overflow-y-auto">
                    {course.modules?.map((m, i) => {
                      const isDone = completedModules.includes(m.module_id);
                      const isActive = activeModule === m.module_id && activeTab === 'study';
                      return (
                        <button key={m.module_id}
                          data-testid={`module-nav-${m.module_id}`}
                          onClick={() => { setActiveModule(m.module_id); setActiveTab('study'); }}
                          className={`w-full text-left p-3 flex items-center gap-3 transition-colors ${
                            isActive ? 'bg-brand-navy/10' : 'hover:bg-slate-50'
                          }`}>
                          {isDone ? (
                            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                          ) : (
                            <Circle className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-brand-navy' : 'text-slate-300'}`} />
                          )}
                          <div className="min-w-0 flex-1">
                            <p className={`text-sm truncate ${isActive ? 'font-medium text-brand-navy' : 'text-slate-700'}`}>{m.title}</p>
                            {m.duration && <p className="text-xs text-slate-400">{m.duration}</p>}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Exam & Certificate nav */}
                  {hasExam && (
                    <div className="border-t">
                      <button data-testid="nav-exam"
                        onClick={() => setActiveTab('exam')}
                        className={`w-full text-left p-3 flex items-center gap-3 transition-colors ${
                          activeTab === 'exam' ? 'bg-brand-navy/10' : 'hover:bg-slate-50'
                        } ${!allModulesComplete ? 'opacity-50' : ''}`}
                        disabled={!allModulesComplete}>
                        <FileText className={`h-5 w-5 ${hasPassed ? 'text-green-500' : activeTab === 'exam' ? 'text-brand-navy' : 'text-slate-400'}`} />
                        <div>
                          <p className={`text-sm ${activeTab === 'exam' ? 'font-medium text-brand-navy' : 'text-slate-700'}`}>
                            {hasPassed ? 'Exam Passed' : 'Take Exam'}
                          </p>
                          {!allModulesComplete && <p className="text-xs text-slate-400">Complete all modules first</p>}
                        </div>
                      </button>
                    </div>
                  )}

                  {hasCertificate && (
                    <div className="border-t">
                      <button data-testid="nav-certificate"
                        onClick={() => setActiveTab('certificate')}
                        className={`w-full text-left p-3 flex items-center gap-3 transition-colors ${
                          activeTab === 'certificate' ? 'bg-brand-navy/10' : 'hover:bg-slate-50'
                        }`}>
                        <Award className="h-5 w-5 text-amber-500" />
                        <p className={`text-sm ${activeTab === 'certificate' ? 'font-medium text-brand-navy' : 'text-slate-700'}`}>Certificate</p>
                      </button>
                    </div>
                  )}
                </Card>
              </div>

              {/* Main content area */}
              <div className="lg:col-span-3">
                {activeTab === 'study' && activeModule && (
                  <>
                    <ModuleView
                      module={course.modules.find(m => m.module_id === activeModule) || {}}
                      isCompleted={completedModules.includes(activeModule)}
                      onComplete={() => handleCompleteModule(activeModule)}
                    />
                    {/* Show rating widget when all modules done and no exam */}
                    {allModulesComplete && !hasExam && (
                      <div className="mt-6">
                        <CourseRatingWidget
                          courseId={courseId}
                          existingRating={course.user_rating}
                          onRated={(data) => setCourse(prev => ({ ...prev, avg_rating: data.avg_rating, rating_count: data.rating_count }))}
                        />
                      </div>
                    )}
                  </>
                )}
                {activeTab === 'exam' && hasExam && (
                  <ExamView
                    exam={course.exam}
                    progress={progress}
                    onSubmit={handleExamSubmit}
                    courseTitle={course.title}
                    courseId={courseId}
                    onRated={(data) => setCourse(prev => ({ ...prev, avg_rating: data.avg_rating, rating_count: data.rating_count }))}
                  />
                )}
                {activeTab === 'certificate' && hasCertificate && (
                  <>
                    <CertificateView certificate={course.certificate} courseTitle={course.title} />
                    <div className="mt-6">
                      <CourseRatingWidget
                        courseId={courseId}
                        existingRating={course.user_rating}
                        onRated={(data) => setCourse(prev => ({ ...prev, avg_rating: data.avg_rating, rating_count: data.rating_count }))}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
            </div>
            <PageSideRail placement={`course:${courseId}`} />
          </div>
        </div>
      </main>
    </div>
  );
}
